import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AppConfig } from 'src/app/app.config';
import { HelpersService } from 'src/app/services';
import { JobcardService } from 'src/app/services/jobcard.service';

@Component({
  selector: 'app-workshop-jobcard-link-services',
  templateUrl: './workshop-jobcard-link-services.component.html',
  styleUrls: ['./workshop-jobcard-link-services.component.scss'],
  providers: [MessageService]
})
export class WorkshopJobcardLinkServicesComponent {

  @Input() linkedServices;
  @Input() linkableServices;
  @Input() jobcardID;
  @Output() linkedServicesUpdated = new EventEmitter();

  public linkingServices = false;
  currencySymbol: string = '$';
  countryData : any = {};
  linkedService: any = {};
  linkedJobsOwner: number | null = null;
  onlyThisCustomer = true;
  searchText = '';
  pageSize = 10;
  sortField = '';
  sortAsc = true;
  first = 0;
  filteredServices: any[] = [];
  pagedServices: any[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private translate: TranslateService,
    private helpersService: HelpersService,
    private appConfig: AppConfig,
    private jobcardService: JobcardService,
    private messageService: MessageService
  ) {

  }
  ngOnInit() {
    this.countryData = this.helpersService.getCountryData();
    this.currencySymbol = this.helpersService.getPartnerData().CurrencySymbol || '$';
    this.linkedService = this.linkedServices.length > 0 ? this.linkedServices[0] : {};
    this.linkedJobsOwner = this.linkedServices.map(s => s.linkedJobsOwner ?? s.LinkedJobsOwner).find(id => id) || null;
    this.linkableServices = [...this.linkableServices || [], ...this.linkedServices.map(s => { return { ...s, selected: true } })].sort((a, b) => a.id - b.id);
    this.applyFilters();
  }

  get linkedCount(): number {
    return this.linkableServices.filter(s => s.selected).length;
  }

  applyFilters() {
    const term = this.searchText.trim().toLowerCase();
    const customerID = this.linkedService.customerID;
    const matchesSearch = (s) => {
      if (!term) return true;
      return [`#${s.jobCardID}`, s.jobCardID, s.customer, s.technician, s.manufacturer, s.model, `${s.manufacturer} ${s.model}`]
        .some(v => v != null && String(v).toLowerCase().includes(term));
    };
    // Linked jobs always come first and ignore the checkbox and sorting, but are filtered by the search
    const linked = this.linkableServices.filter(s => s.selected && matchesSearch(s));
    const others = this.linkableServices.filter(s => {
      if (s.selected) return false;
      if (this.onlyThisCustomer && customerID && s.customerID != customerID) return false;
      return matchesSearch(s);
    });
    if (this.sortField) {
      const dir = this.sortAsc ? 1 : -1;
      others.sort((a, b) => {
        const av = this.getSortValue(a), bv = this.getSortValue(b);
        return (typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' })) * dir;
      });
    }
    this.filteredServices = [...linked, ...others];
    this.first = Math.min(this.first, Math.max(0, (Math.ceil(this.filteredServices.length / this.pageSize) - 1) * this.pageSize));
    this.updatePage();
  }

  sortBy(field: string) {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc = true;
    }
    this.first = 0;
    this.applyFilters();
  }

  sortIcon(field: string) {
    if (this.sortField !== field) return 'pi pi-sort-alt';
    return this.sortAsc ? 'pi pi-sort-amount-up-alt' : 'pi pi-sort-amount-down';
  }

  private getSortValue(s) {
    switch (this.sortField) {
      case 'jobCardID': return Number(s.jobCardID) || 0;
      case 'scheduledDate': return s.scheduledDate ? new Date(s.scheduledDate).getTime() : 0;
      case 'item': return `${s.manufacturer || ''} ${s.model || ''}`;
      case 'status': return this.getStatusLabel(s) || '';
      case 'invoiceTotal': return Number(s.invoiceTotal) || 0;
      default: return s[this.sortField] || '';
    }
  }

  onFilterChanged() {
    this.first = 0;
    this.applyFilters();
  }

  onPageChange(event) {
    this.first = event.first;
    this.updatePage();
  }

  private updatePage() {
    this.pagedServices = this.filteredServices.slice(this.first, this.first + this.pageSize);
  }

  // Stars only show when the linked jobs belong to more than one customer (including ones hidden by the checkbox)
  get showStars(): boolean {
    return new Set(this.linkableServices.filter(s => s.selected).map(s => s.customerID)).size > 1;
  }

  // The primary customer is the owner of the linked jobs
  isFavourite(service): boolean {
    return !!this.linkedJobsOwner && service.customerID === this.linkedJobsOwner;
  }

  // Rows that can't be linked/unlinked: the current job, external jobs and the primary customer's linked jobs
  isLinkLocked(service): boolean {
    return service.id == this.jobcardID || !!service.externalKey || (!!service.selected && this.isFavourite(service));
  }

  getStatusLabel(service) {
    return this.appConfig.getStatusLabel(service.statusID);
  }


  linkSelectedService(service) {
    if (this.isLinkLocked(service)) return;

    this.jobcardService.getLinkJobCard(this.linkedService.partnerID, this.jobcardID, service.id).subscribe((results) => {
      service.selected = !service.selected;
      this.applyFilters();

      const finish = () => {
        this.linkedServicesUpdated.emit();
        this.messageService.add({ life: 5000, key: "linked-services-toast", detail: this.translate.instant(service.selected ? 'bikeshop.modal.update_job_card.job_card.link_service.success_text' : 'bikeshop.modal.update_job_card.job_card.unlink_service.success_text') });
      };

      if (service.selected) {
        // The first job in the group decides the primary customer
        const firstJob = this.linkedServices[0] || this.linkableServices.find(s => s.id == this.jobcardID);
        if (firstJob) {
          // Reload the linked jobs only once the primary customer is saved so the reload picks it up
          this.jobcardService.setFavouriteLinkedServiceCustomer(firstJob.partnerID, firstJob.id, firstJob.customerID).subscribe(() => finish(), () => finish());
          return;
        }
      }
      finish();
    });
  }

  done(){
    this.activeModal.close();
  }

}
